import { z } from 'zod';

import {
  AccessCode,
  AppUser,
  BulkJob,
  ControllerVendor,
  EventNetwork,
  HospitalityEvent,
  Location,
  PmsVendor,
  ProvisionSource,
  Unit,
  Tenant,
  accessCodeSchema,
  appUserSchema,
  bulkJobSchema,
  controllerVendorSchema,
  eventNetworkSchema,
  hospitalityEventSchema,
  locationSchema,
  pmsVendorSchema,
  unitSchema,
  tenantSchema
} from './hospitality';
import type { PageSlice, SortDirection } from '../tables/table.utils';

const pagedResultMetaSchema = z.object({
  page: z.number().int().min(1),
  pageCount: z.number().int().min(0),
  total: z.number().int().min(0),
  from: z.number().int().min(0),
  to: z.number().int().min(0)
});

export function pagedResultSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return pagedResultMetaSchema.extend({
    items: z.array(itemSchema)
  }).transform((page) => ({
    ...page,
    pageCount: Math.max(1, page.pageCount),
    from: page.total === 0 ? 0 : page.from,
    to: page.total === 0 ? 0 : page.to
  }));
}

export type PagedResult<T> = PageSlice<T>;

export interface TableQuery<K extends string> {
  page: number;
  pageSize: number;
  sortKey: K;
  sortDirection: SortDirection;
}

export type UnitListSortKey = 'name' | 'type' | 'vlan' | 'ssid' | 'panMode' | 'capacity';
export type SpaceListSortKey = UnitListSortKey;
export type UnitListSegment = 'all' | 'room' | 'shared';
export type SpaceListSegment = UnitListSegment;

export interface UnitListQuery extends TableQuery<UnitListSortKey> {
  tenantId: string;
  locationId: string;
  segment?: UnitListSegment;
}
export interface SpaceListQuery extends UnitListQuery {}

export const pagedUnitSchema = pagedResultSchema(unitSchema);
export const pagedSpaceSchema = pagedUnitSchema;
export type PagedUnitResult = PagedResult<Unit>;
export type PagedSpaceResult = PagedUnitResult;

export type ActiveCodeSortKey = 'guest' | 'space' | 'validity' | 'source' | 'devices';

export interface ActiveCodesQuery extends TableQuery<ActiveCodeSortKey> {
  tenantId: string;
  locationId: string;
  sourceFilter: 'all' | ProvisionSource;
  search: string;
  codeType: 'hotel';
}

export type ArchivedCodeSortKey = 'guest' | 'source' | 'validity';

export interface ArchivedCodesQuery extends TableQuery<ArchivedCodeSortKey> {
  tenantId: string;
  locationId: string;
  search: string;
  codeType: 'hotel';
}

export const pagedAccessCodeSchema = pagedResultSchema(accessCodeSchema);
export type PagedAccessCodeResult = PagedResult<AccessCode>;

export type EventSortKey = 'name' | 'validity' | 'space' | 'status';

export interface EventsQuery extends TableQuery<EventSortKey> {
  tenantId: string;
  locationId: string;
  status?: 'scheduled' | 'active' | 'expired' | 'disabled';
  search?: string;
}

export const pagedEventSchema = pagedResultSchema(hospitalityEventSchema);
export type PagedEventResult = PagedResult<HospitalityEvent>;

export interface EventNetworksQuery {
  tenantId: string;
  locationId: string;
  page: number;
  pageSize: number;
}

export const pagedEventNetworkSchema = pagedResultSchema(eventNetworkSchema);
export type PagedEventNetworkResult = PagedResult<EventNetwork>;

export type BulkJobSortKey = 'job' | 'createdAt' | 'vlan' | 'state';

export interface BulkJobsQuery extends TableQuery<BulkJobSortKey> {
  locationId: string;
}

export const pagedBulkJobSchema = pagedResultSchema(bulkJobSchema);
export type PagedBulkJobResult = PagedResult<BulkJob>;

export type TenantSortKey = 'name';

export interface TenantsQuery extends TableQuery<TenantSortKey> {}

export const pagedTenantSchema = pagedResultSchema(tenantSchema);
export type PagedTenantResult = PagedResult<Tenant>;

export type UserSortKey = 'name' | 'tenants' | 'locations';

export interface UsersQuery extends TableQuery<UserSortKey> {}

export const pagedUserSchema = pagedResultSchema(appUserSchema);
export type PagedUserResult = PagedResult<AppUser>;

export type LocationSortKey = 'name' | 'pms' | 'controller';

export interface LocationsQuery extends TableQuery<LocationSortKey> {
  tenantId: string;
}

export const pagedLocationSchema = pagedResultSchema(locationSchema);
export type PagedLocationResult = PagedResult<Location>;

export type ControllerVendorSortKey = 'name';

export interface ControllerVendorsQuery extends TableQuery<ControllerVendorSortKey> {}

export const pagedControllerVendorSchema = pagedResultSchema(controllerVendorSchema);
export type PagedControllerVendorResult = PagedResult<ControllerVendor>;

export type PmsVendorSortKey = 'name';

export interface PmsVendorsQuery extends TableQuery<PmsVendorSortKey> {}

export const pagedPmsVendorSchema = pagedResultSchema(pmsVendorSchema);
export type PagedPmsVendorResult = PagedResult<PmsVendor>;
